import { makeAutoObservable } from 'mobx';

import { findItemDeep, removeItem, updateItem } from '@/common/helpers/tree.utils';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem.types';
import { DriveContainerId, DriveItemId } from '@/drive/entities/DriveItemId';
import { DriveFolderOutline, DriveNode } from '@/drive/entities/DriveNode';
import { applyPartialOrder } from '@/drive/helpers/applyPartialOrder';
import { insertTemporaryItems } from '@/drive/helpers/insertTemporaryItems';
import { resolveTemporaryItems } from '@/drive/helpers/resolveTemporaryItems';

export class DriveNodeTree {
  constructor(public roots: DriveNode[]) {
    makeAutoObservable(this, {
      findNode: false,
      hasNode: false,
      findFolder: false,
      getChildren: false,
    });
  }

  findNode(id: DriveItemId) {
    return findItemDeep(this.roots, id);
  }

  hasNode(id: DriveContainerId) {
    return this.isRoot(id) || !!this.findNode(id);
  }

  findFolder(id: DriveContainerId): Nullable<DriveFolderOutline> {
    if (this.isRoot(id)) {
      return null;
    }

    const node = this.findNode(id);

    return node?.type === DriveItemType.Folder ? node : null;
  }

  getChildren(parentId: DriveContainerId): Nullable<DriveNode[]> {
    if (this.isRoot(parentId)) {
      return this.roots;
    } else {
      return this.findFolder(parentId)?.children ?? null;
    }
  }

  reorder(parentId: DriveContainerId, orderedIds: DriveItemId[]) {
    this.updateChildren(parentId, (children) => applyPartialOrder(children, orderedIds));
  }

  insertTemporaryNodes(
    parentId: DriveContainerId,
    temporaryNodes: DriveNode[],
    precedingNodeIdByTempId: Map<DriveItemId, DriveItemId>,
  ) {
    if (temporaryNodes.length > 0) {
      this.updateChildren(parentId, (nodes) => insertTemporaryItems(nodes, temporaryNodes, precedingNodeIdByTempId));
    }
  }

  resolveTemporaryNodes(parentId: DriveContainerId, resolvedNodeByTempId: Map<DriveItemId, Nullable<DriveNode>>) {
    this.updateChildren(parentId, (children) => resolveTemporaryItems(children, resolvedNodeByTempId));
  }

  move(node: DriveNode, parentId: DriveContainerId, previousParentId: DriveContainerId) {
    if (!this.getChildren(parentId)) {
      return;
    }

    const movedNode = this.findNode(node.id) ?? node;

    this.removeNode(movedNode.id);
    this.updateChildren(parentId, (children) => [movedNode, ...children.filter((child) => child.id !== movedNode.id)]);

    if (previousParentId !== parentId) {
      this.changeFolderCount(previousParentId, -1);
      this.changeFolderCount(parentId, 1);
    }
  }

  removeNode(id: DriveItemId) {
    this.roots = removeItem(this.roots, id);
  }

  updateNode(node: DriveNode) {
    const currentFolder = this.findFolder(node.id);

    if (currentFolder && node.type === DriveItemType.Folder) {
      this.roots = updateItem(this.roots, { ...node, children: currentFolder.children });
    } else {
      this.roots = updateItem(this.roots, node);
    }
  }

  private updateChildren(parentId: DriveContainerId, update: (children: DriveNode[]) => DriveNode[]) {
    if (this.isRoot(parentId)) {
      this.roots = update(this.roots);
    } else {
      const folder = this.findFolder(parentId);

      if (folder) {
        folder.children = update(folder.children);
        this.roots = [...this.roots];
      }
    }
  }

  private changeFolderCount(containerId: DriveContainerId, diff: number) {
    const folder = this.findFolder(containerId);

    if (folder) {
      folder.count = Math.max(0, folder.count + diff);
    }
  }

  private isRoot(id: DriveContainerId): id is null {
    return id === null;
  }
}
